// src/hooks/useXeroBalance.ts
// ─────────────────────────────────────────────────────────────────────────────
// Wraps /api/xero/balance. Mirrors useXeroData's shape.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';

export interface XeroBalance {
  cash: number;
  tradeDebtors: number;
  otherDebtors: number;
  prepayments: number;
  stock: number;
  dlaBalance: number;                  // + = asset, − = liability
  fixedAssets: number;
  goodwill: number;
  vatLiability: number;
  ctLiability: number;
  payeLiability: number;
  staffPensionLiability: number;
  hirePurchase: number;
  bounceBackLoan: number;
  accruals: number;
  shareCapital: number;
  retainedEarningsOpening: number;
  profitInPeriod: number;
  dividendsPaid: number;
  retainedEarningsClosing: number;
}

export interface XeroBalanceResult {
  loading: boolean;
  data: XeroBalance | null;
  error: string | null;
  tenantName: string | null;
}

export function useXeroBalance(tenantId: string | undefined): XeroBalanceResult {
  const [result, setResult] = useState<XeroBalanceResult>({
    loading: true,
    data: null,
    error: null,
    tenantName: null,
  });

  useEffect(() => {
    if (!tenantId) {
      setResult({ loading: false, data: null, error: 'No Xero tenant bound to this client', tenantName: null });
      return;
    }
    const q = `?tenantId=${encodeURIComponent(tenantId)}`;
    Promise.all([
      fetch(`/api/xero/status${q}`).then((r) => r.json()),
      fetch(`/api/xero/balance${q}`).then((r) => r.json()),
    ])
      .then(([status, balance]) => {
        if (!status.connected) {
          setResult({ loading: false, data: null, error: 'Xero not connected', tenantName: null });
          return;
        }
        if (balance.error) {
          setResult({ loading: false, data: null, error: balance.error, tenantName: status.tenantName });
          return;
        }
        setResult({ loading: false, data: balance as XeroBalance, error: null, tenantName: status.tenantName });
      })
      .catch((err) => {
        setResult({ loading: false, data: null, error: err.message, tenantName: null });
      });
  }, [tenantId]);

  return result;
}
